/**
 * Controls Tab Component
 * Control palette with categorized controls
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useUIStore } from '@atlas-forms/state-react';

import type { FormSchema, FormControl, FormControlType } from '@atlas-forms/types-js';
import { groupControlsByCategory, getDefaultLabel, getControlLabel } from '../../utils/controlUtils';
import { getControlHelpInfo } from '../../utils/controlHelpTexts';
import '../../styles/tabs/ControlsTab.css';
import {
  FileIcon, CodeIcon, EditIcon, SearchIcon, UploadIcon, EyeOffIcon,
  CheckIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon, BarChartIcon,
  HistogramIcon, WaterfallChartIcon, LineChartIcon, ScatterPlotIcon,
  AreaChartIcon, HeatmapIcon, GaugeIcon,
  PieChartIcon, PaletteIcon, LayersIcon, GridIcon, DatabaseIcon, FilterIcon,
  RefreshIcon, TagIcon, SettingsIcon, FolderIcon, MenuIcon, ExternalLinkIcon,
  PlayIcon, FormIcon, AlertIcon,
} from '@atlas-forms/ui-components-react';
import {
  TabsLeftIcon,
  TabsTopIcon,
  AccordionIcon,
  WizardIcon,
  CardBasedIcon,
  SidebarNavIcon,
} from '../Icons/PropertiesIconSet';
import { HelpTooltip } from '../HelpTooltip/HelpTooltip';

export interface ControlsTabProps {
  onAddControl: (control: FormControl) => void;
  schema: FormSchema;
}

/**
 * Controls Tab - Control palette for adding controls to form
 */
export const ControlsTab: React.FC<ControlsTabProps> = ({ onAddControl, schema }) => {
  const { theme } = useUIStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const controlCategories = useMemo(() => groupControlsByCategory(), []);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return controlCategories;

    const term = searchTerm.toLowerCase();
    const filtered: Record<string, FormControlType[]> = {};
    Object.entries(controlCategories).forEach(([category, controls]) => {
      const filteredControls = controls.filter((control) =>
        control.toLowerCase().includes(term) ||
        getDefaultLabel(control).toLowerCase().includes(term)
      );
      if (filteredControls.length > 0) {
        filtered[category] = filteredControls;
      }
    });
    return filtered;
  }, [controlCategories, searchTerm]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const handleAddControl = useCallback(
    (controlType: FormControlType) => {
      const newControl: FormControl = {
        id: `control-${Date.now()}`,
        type: controlType,
        label: getDefaultLabel(controlType),
        order: schema.controls.length,
        required: false,
        readonly: false,
        disabled: false,
        hidden: false,
      };
      onAddControl(newControl);
    },
    [schema.controls.length, onAddControl]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLButtonElement>, controlType: FormControlType) => {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('application/json', JSON.stringify({
        type: 'new-control',
        controlType,
      }));
    },
    []
  );

  /**
   * Build help content for a control
   */
  const buildHelpContent = (helpInfo: any): React.ReactNode => {
    return (
      <>
        <p>{helpInfo.description}</p>
        {helpInfo.useCases && helpInfo.useCases.length > 0 && (
          <>
            <strong style={{ display: 'block', marginTop: '6px', marginBottom: '4px' }}>
              Best for:
            </strong>
            <ul>
              {helpInfo.useCases.map((useCase: string, idx: number) => (
                <li key={idx}>{useCase}</li>
              ))}
            </ul>
          </>
        )}
        {helpInfo.tips && helpInfo.tips.length > 0 && (
          <>
            <strong style={{ display: 'block', marginTop: '6px', marginBottom: '4px' }}>
              Tips:
            </strong>
            <ul>
              {helpInfo.tips.map((tip: string, idx: number) => (
                <li key={idx}>{tip}</li>
              ))}
            </ul>
          </>
        )}
      </>
    );
  };

  const handleAddPreConfiguredContainer = useCallback(
    (template: 'two-step-tabs' | 'multi-step-tabs' | 'accordion' | 'wizard' | 'card-based' | 'sidebar-nav') => {
      const now = Date.now();
      const containerConfig: any = {
        id: `container-${now}`,
        description: 'Pre-configured form container',
      };

      let label = '';

      switch (template) {
        case 'two-step-tabs':
          label = 'Multi-Step Form (Tabs - Left)';
          containerConfig.layout = 'tabs-left';
          containerConfig.layoutConfig = { tabWidth: '160px', defaultActiveIndex: 0 };
          break;
        case 'multi-step-tabs':
          label = 'Multi-Step Form (Tabs - Top)';
          containerConfig.layout = 'tabs-top';
          containerConfig.layoutConfig = { defaultActiveIndex: 0 };
          break;
        case 'accordion':
          label = 'Multi-Step Form (Accordion)';
          containerConfig.layout = 'accordion';
          break;
        case 'wizard':
          label = 'Multi-Step Form (Wizard)';
          containerConfig.layout = 'wizard';
          break;
        case 'card-based':
          label = 'Multi-Step Form (Card-Based)';
          containerConfig.layout = 'tabs-top';
          containerConfig.layoutConfig = { style: 'card', defaultActiveIndex: 0 };
          break;
        case 'sidebar-nav':
          label = 'Multi-Step Form (Sidebar Nav)';
          containerConfig.layout = 'tabs-left';
          containerConfig.layoutConfig = { tabWidth: '180px', style: 'sidebar', defaultActiveIndex: 0 };
          break;
      }

      containerConfig.childForms = [];
      containerConfig.validationMode = 'free';
      containerConfig.validationConfig = { validateBeforeSwitch: false, showErrorBadges: true };
      containerConfig.showPreviousButton = true;
      containerConfig.showNextButton = true;
      containerConfig.showSubmitButton = true;

      const newControl: FormControl = {
        id: `control-${now}`,
        type: 'form-container',
        label,
        order: schema.controls.length,
        required: false,
        readonly: false,
        disabled: false,
        hidden: false,
        config: containerConfig,
      };
      onAddControl(newControl);
    },
    [schema.controls.length, onAddControl]
  );

  return (
    <div className="af-controls-tab" data-theme={theme}>
      <div className="af-controls-tab__search">
        <input
          type="text"
          placeholder="Search controls..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="af-controls-tab__search-input"
        />
      </div>

      <div className="af-controls-tab__categories">
        {/* Pre-configured Form Containers — filtered by search */}
        {(() => {
          const containerItems = [
            { key: 'two-step-tabs', name: 'Tabs (Left)', icon: <TabsLeftIcon size={16} color="#666" />, title: 'Click to add multi-step form with left side tabs', helpTitle: 'Tabs (Left)', helpContent: <><p>Vertical tabs on the left side. Each tab shows a different form.</p><ul><li><strong>Best for:</strong> Organized multi-step wizards</li><li><strong>Tab width:</strong> Customizable (default: 160px)</li><li><strong>Forms:</strong> Displayed in tab order</li></ul></> },
            { key: 'multi-step-tabs', name: 'Tabs (Top)', icon: <TabsTopIcon size={16} color="#666" />, title: 'Click to add multi-step form with top tabs', helpTitle: 'Tabs (Top)', helpContent: <><p>Horizontal tabs at the top. Compact and web-standard.</p><ul><li><strong>Best for:</strong> Forms with few steps</li><li><strong>Space efficient:</strong> Minimal vertical footprint</li><li><strong>Familiar:</strong> Users expect top tabs</li></ul></> },
            { key: 'accordion', name: 'Accordion', icon: <AccordionIcon size={16} color="#666" />, title: 'Click to add accordion-style multi-step form', helpTitle: 'Accordion', helpContent: <><p>Expandable sections. Only one form visible at a time.</p><ul><li><strong>Best for:</strong> Many optional sections</li><li><strong>Space:</strong> Very compact layout</li><li><strong>Progress:</strong> Shows validation badges per section</li></ul></> },
            { key: 'wizard', name: 'Wizard', icon: <WizardIcon size={16} color="#666" />, title: 'Click to add wizard-style multi-step form', helpTitle: 'Wizard', helpContent: <><p>Linear step-by-step progression with Previous/Next/Submit buttons.</p><ul><li><strong>Best for:</strong> Guided workflows</li><li><strong>Controls:</strong> Previous, Next, Skip, Submit buttons</li><li><strong>Validation:</strong> Can block progression on errors</li></ul></> },
            { key: 'card-based', name: 'Card-Based', icon: <CardBasedIcon size={16} color="#666" />, title: 'Click to add card-based multi-step form', helpTitle: 'Card-Based', helpContent: <><p>Modern card-style tabs. Each form in its own card container.</p><ul><li><strong>Best for:</strong> Contemporary UX design</li><li><strong>Visual:</strong> Card-style with elevation</li><li><strong>Responsive:</strong> Adapts to screen size</li></ul></> },
            { key: 'sidebar-nav', name: 'Sidebar Nav', icon: <SidebarNavIcon size={16} color="#666" />, title: 'Click to add sidebar navigation multi-step form', helpTitle: 'Sidebar Navigation', helpContent: <><p>Left sidebar with form navigation. Professional dashboard style.</p><ul><li><strong>Best for:</strong> Application-like forms</li><li><strong>Width:</strong> Wider sidebar (180px default)</li><li><strong>Style:</strong> Dashboard/admin panel aesthetic</li></ul></> },
          ];
          const visibleContainers = searchTerm
            ? containerItems.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.key.toLowerCase().includes(searchTerm.toLowerCase()))
            : containerItems;
          if (visibleContainers.length === 0) return null;
          return (
        <div className="af-controls-tab__category">
          <button
            className={`af-controls-tab__category-header ${
              expandedCategories.has('Form Containers') ? 'af-controls-tab__category-header--expanded' : ''
            }`}
            onClick={() => toggleCategory('Form Containers')}
          >
            <span className="af-controls-tab__category-toggle">{expandedCategories.has('Form Containers') ? <ChevronDownIcon width={14} height={14} /> : <ChevronRightIcon width={14} height={14} />}</span>
            <span className="af-controls-tab__category-name" title="Form Containers">Form Containers</span>
            <span className="af-controls-tab__category-count">({visibleContainers.length})</span>
          </button>

          {expandedCategories.has('Form Containers') && (
            <div className="af-controls-tab__category-controls">
              {visibleContainers.map(item => (
              <button
                key={item.key}
                onClick={() => handleAddPreConfiguredContainer(item.key as any)}
                className="af-controls-tab__control-item"
                title={item.title}
              >
                <div className="af-controls-tab__control-item-content">
                  <span className="af-controls-tab__control-icon">
                    {item.icon}
                  </span>
                  <span className="af-controls-tab__control-name">{item.name}</span>
                </div>
                <div className="af-controls-tab__control-item-help">
                  <HelpTooltip
                    title={item.helpTitle}
                    content={item.helpContent}
                    position="left"
                    iconSize={12}
                    iconColor="#6B7879"
                  />
                </div>
              </button>
              ))}
            </div>
          )}
        </div>
          );
        })()}

        {/* Regular controls */}
        {Object.entries(filteredCategories).map(([category, controls]) => (
          <div key={category} className="af-controls-tab__category">
            <button
              className={`af-controls-tab__category-header ${
                expandedCategories.has(category) ? 'af-controls-tab__category-header--expanded' : ''
              }`}
              onClick={() => toggleCategory(category)}
            >
              <span className="af-controls-tab__category-toggle">{expandedCategories.has(category) ? <ChevronDownIcon width={14} height={14} /> : <ChevronRightIcon width={14} height={14} />}</span>
              <span className="af-controls-tab__category-name" title={category}>{category}</span>
              <span className="af-controls-tab__category-count">({controls.length})</span>
            </button>

            {expandedCategories.has(category) && (
              <div className="af-controls-tab__category-controls">
                {controls.map((controlType) => {
                  const helpInfo = getControlHelpInfo(controlType);
                  return (
                    <button
                      key={controlType}
                      draggable
                      onDragStart={(e) => handleDragStart(e, controlType)}
                      onClick={() => handleAddControl(controlType)}
                      className="af-controls-tab__control-item"
                      title={`Click to add, or drag to canvas`}
                    >
                      <div className="af-controls-tab__control-item-content">
                        <span className="af-controls-tab__control-icon">
                          {getControlIcon(controlType)}
                        </span>
                        <span className="af-controls-tab__control-name">
                          {getControlLabel(controlType)}
                        </span>
                      </div>
                      {helpInfo && (
                        <div className="af-controls-tab__control-item-help">
                          <HelpTooltip
                            title={helpInfo.title}
                            content={buildHelpContent(helpInfo)}
                            position="left"
                            iconSize={12}
                            iconColor="#6B7879"
                          />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="af-controls-tab__info">
        <p className="af-controls-tab__info-text">
          Click a control to add it, or drag it to the canvas to place it at a specific location.
        </p>
      </div>
    </div>
  );
};

ControlsTab.displayName = 'ControlsTab';

/**
 * Get icon for control type
 */
function getControlIcon(type: string): React.ReactNode {
  if (['text', 'textarea', 'email', 'url', 'password', 'rich-text-editor'].includes(type)) return <EditIcon width={16} height={16} />;
  if (['checkbox', 'radio', 'switch'].includes(type)) return <CheckIcon width={16} height={16} />;
  if (['select', 'multiselect', 'tree-select', 'cascading-select', 'multi-select-search'].includes(type)) return <ChevronDownIcon width={16} height={16} />;
  if (['date', 'datetime', 'date-range-picker', 'time-picker', 'timeline', 'gantt-chart'].includes(type)) return <ClockIcon width={16} height={16} />;
  if (['file-upload', 'document-scanner', 'js-file-selector'].includes(type)) return <UploadIcon width={16} height={16} />;
  if (type === 'histogram') return <HistogramIcon width={16} height={16} />;
  if (type === 'waterfall-chart') return <WaterfallChartIcon width={16} height={16} />;
  if (type === 'line-chart') return <LineChartIcon width={16} height={16} />;
  if (type === 'area-chart') return <AreaChartIcon width={16} height={16} />;
  if (type === 'scatter-plot') return <ScatterPlotIcon width={16} height={16} />;
  if (['heatmap', 'tree-map'].includes(type)) return <HeatmapIcon width={16} height={16} />;
  if (['circular-gauge', 'linear-gauge', 'progress-ring'].includes(type)) return <GaugeIcon width={16} height={16} />;
  if (['bar-chart', 'analytics-dashboard', 'kpi-card', 'mermaid', 'sankey-diagram', 'network-graph', 'slider-range'].includes(type)) return <BarChartIcon width={16} height={16} />;
  if (['pie-chart', 'bubble-chart'].includes(type)) return <PieChartIcon width={16} height={16} />;
  if (['data-table', 'grid', 'table'].includes(type)) return <GridIcon width={16} height={16} />;
  if (['database-query-builder', 'api-response-viewer', 'variable-inspector'].includes(type)) return <DatabaseIcon width={16} height={16} />;
  if (['json-editor', 'enhanced-json-editor', 'code-editor', 'javascript-block', 'html', 'html-block', 'css', 'style-block', 'conditional-logic-viewer', 'key-value-pairs'].includes(type)) return <CodeIcon width={16} height={16} />;
  if (['color-picker', 'css-file-selector'].includes(type)) return <PaletteIcon width={16} height={16} />;
  if (['card-container', 'collapsible-panel', 'accordion', 'enhanced-tabs', 'modal', 'tab', 'form-container'].includes(type)) return <LayersIcon width={16} height={16} />;
  if (['sidebar-nav', 'menu'].includes(type)) return <MenuIcon width={16} height={16} />;
  if (['label', 'header', 'tag-input'].includes(type)) return <TagIcon width={16} height={16} />;
  if (['button', 'stepper'].includes(type)) return <PlayIcon width={16} height={16} />;
  if (['image', 'image-gallery', 'asset-manager', 'pdf-viewer', 'document-viewer'].includes(type)) return <FolderIcon width={16} height={16} />;
  if (['link', 'breadcrumb', 'iframe-viewer'].includes(type)) return <ExternalLinkIcon width={16} height={16} />;
  if (['search', 'location-picker', 'form-picker'].includes(type)) return <SearchIcon width={16} height={16} />;
  if (type === 'custom') return <SettingsIcon width={16} height={16} />;
  if (type === 'number') return <BarChartIcon width={16} height={16} />;
  if (type.includes('chart')) return <BarChartIcon width={16} height={16} />;
  if (type.includes('graph')) return <ScatterPlotIcon width={16} height={16} />;
  return <FileIcon width={16} height={16} />;
}

// getDefaultLabel and getControlLabel imported from ../../utils/controlUtils
